/// <amd-module name="Comments/Viewer/Components/CommentViewer" />
import { IComment } from "Comments/Common/Contracts";
import { ICommentReactionSelectorProps } from "Comments/Reactions/Components/CommentReactionSelector.Props";
import { ICommentReactionStatusBarProps } from "Comments/Reactions/Components/CommentReactionStatusBar.Props";
import { ICommentViewerProps } from "Comments/Viewer/Components/CommentViewer.Props";
import { ICommentViewerToolbarProps } from "Comments/Viewer/Components/CommentViewerToolbar.Props";
import * as Resources from "Comments/Viewer/Resources.Viewer";
import { ObservableValue } from "VSS/Core/Observable";
import * as StringUtil from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Observer } from "VSSUI/Observer";
import * as PlatformResources from "VSSUI/Resources.Core";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, noop } from "VSSUI/Util";
import * as DateUtil from "VSSUI/Utilities/Date";
import { openImageInNewTab } from "VSSUI/Utilities/Image";
import * as React from "react";
import { IFilterXSSOptions, escapeAttrValue, filterXSS } from "xss";
import { superAllowList } from "xss/PresetOptions";

const toolbarDependencies = ["ms.vss-comments.viewer-toolbar"];
const reactionsDependencies = ["ms.vss-comments.reactions"];

const htmlSanitizerPreserveClassAndMentionOptions: IFilterXSSOptions = {
    ...superAllowList,
    onIgnoreTagAttr: function (tag, name, value) {
        if (name.startsWith("class") || name.startsWith("data-vss-mention")) {
            return name + '="' + escapeAttrValue(value) + '"';
        }
    },
    stripIgnoreTagBody: ["head", "style"],
    onIgnoreTag: function (tag) {
        if (["html", "body"].includes(tag)) {
            return "";
        }
    },
    onTagAttr: (tag, name, value) => {
        if (name === "href" && value.startsWith("x-mvwit:workitem/")) {
            return name + '="' + escapeAttrValue(value) + '"';
        }
    }
};

const locationHashCharacter = "#";

/**
 * Viewer for comment.
 */
export class CommentViewer extends VssComponent<ICommentViewerProps> {
    /**
     * Attribute name for the comment ID.
     */
    public static readonly CommentIdAttribute = "data-comment-id";

    private _additionalContentProps: { [key: string]: string | number } = {};
    private _commentContentRef = React.createRef<HTMLDivElement>();
    private _showDarkThemeFormat = new ObservableValue<boolean>(false);
    private _themeService: IVssThemeService;

    constructor(props: ICommentViewerProps, context: IVssComponentContext) {
        super(props, context);

        this._additionalContentProps = { [CommentViewer.CommentIdAttribute]: props.comment.id };
        this._themeService = this.context.pageContext.getService("IVssThemeService");
    }

    public render(): JSX.Element {
        const {
            comment,
            className,
            contentClassName,
            getAnchorId = this._getAnchorId,
            onRenderPersona,
            isReactionsEnabled,
            onRenderCommentBody,
            tooltipTimeFormat,
            currentDate,
            doNotHighlight
        } = this.props;
        const createdDate = comment.createdOnBehalfDate || comment.createdDate;
        const editedDate = comment.modifiedDate;
        const identity = comment.createdOnBehalfOf || comment.createdBy;
        const isEdited = comment.createdDate.getTime() !== comment.modifiedDate.getTime();
        const timestamp =
            DateUtil.ago(createdDate, undefined, currentDate) === PlatformResources.JustNow
                ? Resources.CommentedJustNow
                : DateUtil.ago(createdDate, undefined, currentDate);
        const __html = this._getSanitizedText(comment);
        const highlightedClass = !doNotHighlight && location.hash === locationHashCharacter + comment.id && "highlighted";

        const createdTooltipText = DateUtil.tooltipString(createdDate);
        const editedTooltipText = StringUtil.format(Resources.CommentEditedTooltip, DateUtil.tooltipString(createdDate), DateUtil.tooltipString(editedDate, undefined, tooltipTimeFormat));

        return (
            <div
                id={getAnchorId && getAnchorId(comment.id)}
                className={css("comment-viewer", className, highlightedClass)}
                {...this._additionalContentProps}
            >
                <div className="comment-header">
                    <div className="comment-header-main">
                        {onRenderPersona && onRenderPersona(identity)}
                        <span className="user-display-name">{identity.displayName}</span>
                        <Tooltip text={isEdited ? editedTooltipText : createdTooltipText}>
                            <a className="comment-timestamp" href={locationHashCharacter + comment.id} target="_blank">
                                {StringUtil.format(Resources.CommentedFormat, timestamp)}
                                { isEdited && <span>&nbsp;{Resources.CommentEditedTag}</span>}
                            </a>
                        </Tooltip>
                    </div>
                    {this._renderToolbar()}
                </div>
                <Observer showDarkThemeFormat={this._showDarkThemeFormat} themeService={this._themeService}>
                    {(observerProps: { showDarkThemeFormat: boolean }) => {
                        const overrideDarkThemeFormatting =
                            !observerProps.showDarkThemeFormat && this._themeService.getCurrentTheme().isDark;
                        return onRenderCommentBody ? (
                            <div ref={this._commentContentRef}>{onRenderCommentBody(comment.text)}</div>
                        ) : (
                            <div
                                className={css(
                                    "comment-content",
                                    overrideDarkThemeFormatting && "comment-content-dark-theme-override",
                                    contentClassName
                                )}
                                ref={this._commentContentRef}
                                dangerouslySetInnerHTML={{ __html }}
                                onDoubleClick={openImageInNewTab}
                            />
                        );
                    }}
                </Observer>

                {isReactionsEnabled && this._hasReactions() && this._renderReactionStatusBar()}
            </div>
        );
    }

    public componentDidMount(): void {
        this._updateContent();

        // Scroll the deep-linked comment into view if any
        if (location.hash === locationHashCharacter + this.props.comment.id) {
            // Timeout lets images potentially load. 250ms still seems responsive
            window.setTimeout(() => this._commentContentRef?.current?.scrollIntoView({ behavior: "smooth" }), 250);
        }
    }

    public componentDidUpdate(): void {
        this._updateContent();
    }

    private _renderToolbar(): JSX.Element {
        const {
            comment: { id: commentId },
            baseUrl,
            readonly,
            canEdit,
            editDisabled,
            formatDisabled,
            onStartEdit,
            onConfirmDelete,
            onMoreClick,
            getPermalink,
            isReactionsEnabled,
            reactionTypeToInfoMap = {},
            editButtonRef,
            onAddReactionButtonClick,
            onAddReaction = noop,
            onDeleteReaction = noop
        } = this.props;

        return (
            <WrappedComponent<ICommentViewerToolbarProps>
                dependencies={toolbarDependencies}
                wrappedType={"comment-viewer-toolbar"}
                wrappedProps={{
                    commentId,
                    baseUrl,
                    readonly,
                    canEdit,
                    editDisabled,
                    formatDisabled,
                    editButtonRef,
                    onStartEdit,
                    onConfirmDelete,
                    onMoreClick,
                    getPermalink: getPermalink ? this._getPermaLink : undefined,
                    showDarkThemeFormat: this._showDarkThemeFormat,
                    reactionSelectorProps: isReactionsEnabled
                        ? ({
                              readonly,
                              commentId,
                              reactionTypeToInfoMap,
                              onAddReactionButtonClick,
                              onAddReaction,
                              onDeleteReaction
                          } as ICommentReactionSelectorProps)
                        : undefined
                }}
            />
        );
    }

    private _renderReactionStatusBar(): JSX.Element {
        const {
            comment: { id: commentId },
            readonly,
            reactionTypeToInfoMap = {},
            onAddReactionButtonClick,
            onAddReaction = noop,
            onDeleteReaction = noop,
            onGetRecentlyReactedUsers = noop
        } = this.props;

        return (
            <div className="reaction-statusbar-placeholder">
                <WrappedComponent<ICommentReactionStatusBarProps>
                    dependencies={reactionsDependencies}
                    wrappedType="comment-reaction-statusbar"
                    wrappedProps={{
                        commentId: commentId,
                        readonly,
                        onAddReactionButtonClick,
                        onAddReaction,
                        onDeleteReaction,
                        reactionTypeToInfoMap,
                        onGetRecentlyReactedUsers
                    }}
                />
            </div>
        );
    }

    private _hasReactions(): boolean {
        const { reactionTypeToInfoMap } = this.props;
        if (!reactionTypeToInfoMap) {
            return false;
        }

        for (const key in reactionTypeToInfoMap) {
            if (reactionTypeToInfoMap[key].totalCount > 0) {
                return true;
            }
        }

        return false;
    }

    private _getAnchorId(commentId: number): string {
        return `comment-${commentId}`;
    }

    private _getPermaLink = (commentId: number): string => {
        const { getPermalink, getAnchorId = this._getAnchorId } = this.props;
        return getPermalink!(getAnchorId(commentId));
    };

    private _updateContent(): void {
        const div = this._commentContentRef.current;
        if (div && div.innerHTML) {
            const { onPostRenderContent } = this.props;
            onPostRenderContent && onPostRenderContent(div.innerHTML, div);
        }
    }

    private _getSanitizedText(comment: IComment): string {
        if (comment.renderedText) {
            // This is a markdown comment.
            // Preserve class names for markdown comments,
            // to ensure checklists and other md specific features work
            return filterXSS(comment.renderedText, htmlSanitizerPreserveClassAndMentionOptions);
        }

        // Sanitize html comments
        return filterXSS(comment.text, htmlSanitizerPreserveClassAndMentionOptions);
    }
}

VssComponent.register("comment-viewer", CommentViewer);
